import { useEffect, useState } from 'react';

export enum MIN_WIDTH {
  SM = 0,
  MD = 636,
  LG = 769,
  XL = 928,
}

/**
 * Sets up a listener for the lifecycle of the component to determine if a
 * media query matches.
 *
 * @param minWidthOrQuery A value from the `MIN_WIDTH` enum or a custom query.
 *
 * @return `true` if the query matches, otherwise `false`.
 */
export const useMedia = (minWidthOrQuery: MIN_WIDTH | string): boolean => {
  const MEDIA_QUERY = Object.values(MIN_WIDTH).includes(minWidthOrQuery)
    ? `(min-width: ${minWidthOrQuery}px)`
    : (minWidthOrQuery as string);

  const [matches, setMatches] = useState<boolean>(
    window.matchMedia(MEDIA_QUERY).matches,
  );

  useEffect(() => {
    const media = window.matchMedia(MEDIA_QUERY);
    const listener = () => setMatches(media.matches);

    // Safari 14 below backward compatibility. https://developer.mozilla.org/en-US/docs/Web/API/MediaQueryList#:~:text=MediaQueryList%20inherits-,EventTarget,-39
    if (media.addEventListener) {
      media.addEventListener('change', listener);
    } else {
      media.addListener(listener);
    }

    return () => {
      if (media.removeEventListener) {
        media.removeEventListener('change', listener);
      } else {
        media.removeListener(listener);
      }
    };
  }, []);

  return matches;
};
